import * as React from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItem from 'LuxKit/List/ListItem'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { syncUserDetails } from 'actions/AuthActions'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import type { VisibleLanguageOption } from 'i18n/util/locale'
import styled from 'styled-components'
import SpoofCustomerLanguageButton from './SpoofCustomerLanguageButton'
import ModalProvider from 'contexts/ModalProvider'
import SpoofTargetLanguageProvider from './SpoofTargetLanguageProvider'

const LanguageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > :first-child {
    flex: 1;
    min-inline-size: 0;
  }
`

const SpoofLanguageLabel = styled(ListItem)<{ $selected: boolean }>`
  background-color: ${props => props.$selected
    ? props.theme.palette.highlight.primary.lightBackground
    : undefined};
`

const SpoofLanguageActionsContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`

const PreReleaseSeparator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-block: 18px 8px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const PreReleaseSeparatorLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

function comparePreReleaseLanguages(
  left: VisibleLanguageOption,
  right: VisibleLanguageOption,
): number {
  return left.nativeName.localeCompare(right.nativeName, undefined, { sensitivity: 'base' })
}

function PreReleaseLanguageSeparator() {
  const intl = useIntl()

  return (
    <PreReleaseSeparator role="separator">
      <PreReleaseSeparatorLabel>
        <BodyText variant="small" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'mTWgcPP3tNwNRu-dXo2i',
            defaultMessage: 'Staff only',
            description: 'English-only label on the divider above pre-release languages in the language picker. Keep the English words in every locale.',
          })}
        </BodyText>
        <LineAngleDownIcon size="XS" colour="light-foreground-alt" aria-hidden />
      </PreReleaseSeparatorLabel>
    </PreReleaseSeparator>
  )
}

function SpoofLanguageActions({
  language,
  onChangeOnlyForSpoof,
}: {
  language: VisibleLanguageOption
  onChangeOnlyForSpoof: (language: VisibleLanguageOption) => void
}) {
  return <SpoofTargetLanguageProvider locale={language.locale}>
    <SpoofLanguageActionButtons
      language={language}
      onChangeOnlyForSpoof={onChangeOnlyForSpoof}
    />
  </SpoofTargetLanguageProvider>
}

function SpoofLanguageActionButtons({
  language,
  onChangeOnlyForSpoof,
}: {
  language: VisibleLanguageOption
  onChangeOnlyForSpoof: (language: VisibleLanguageOption) => void
}) {
  const intl = useIntl()

  return <SpoofLanguageActionsContainer>
    <TextButton kind="secondary" size="small" onClick={() => onChangeOnlyForSpoof(language)}>
      {intl.formatMessage({
        id: 'hgQdrn1A0o8K-bCHHSU_',
        defaultMessage: 'Change only for me',
        description: 'Action that changes the language only for the spoofing agent',
      })}
    </TextButton>
    <SpoofCustomerLanguageButton language={language} onChangeOnlyForSpoof={onChangeOnlyForSpoof} />
  </SpoofLanguageActionsContainer>
}

function NavBarLanguageItems(props: {
  onLanguageSelect?: (changed: boolean) => void
}) {
  const { onLanguageSelect } = props
  const dispatch = useAppDispatch()
  const {
    locale,
    languageOptions,
    preReleaseLanguageOptions,
  } = useLocaleUrlOptions()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const preReleaseLocales = new Set(preReleaseLanguageOptions.map(option => option.locale))
  const releasedLanguages = languageOptions.filter(language => !preReleaseLocales.has(language.locale))
  const preReleaseLanguages = preReleaseLanguageOptions.toSorted(comparePreReleaseLanguages)
  const showPreReleaseSeparator = releasedLanguages.length > 0 && preReleaseLanguages.length > 0

  const currentLanguage =
    languageOptions.find((lang) => lang.locale === locale) ??
    languageOptions[0]
  const onLanguageChange = useCallback((selectedLanguage: VisibleLanguageOption) => {
    const changed = selectedLanguage.locale !== currentLanguage?.locale
    if (changed) {
      setLanguageCookie(selectedLanguage.locale)
      if (isLoggedIn && !isSpoofed) {
        updateCustomerLanguagePreferenceFromAuth(selectedLanguage.locale)
          .catch(reportClientError)
        dispatch(syncUserDetails({ language: selectedLanguage.locale }))
      }
      const currentLocationUrl = UrlLocation.fromLocation(window.location)
      const redirect = currentLocationUrl.getRedirectToLocale({
        targetLocale: selectedLanguage.locale,
        languageOptions,
      })
      const targetUrl = currentLocationUrl.withRedirect(redirect)

      if (!targetUrl.equals(currentLocationUrl)) {
        window.location.href = targetUrl.toString()
      }
    }
    onLanguageSelect?.(changed)
  }, [dispatch, currentLanguage?.locale, languageOptions, onLanguageSelect, isLoggedIn, isSpoofed])

  const onLanguageClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const selectedLanguage = languageOptions.find(
      (language) => language.locale === e.currentTarget.dataset.locale,
    )
    if (selectedLanguage) onLanguageChange(selectedLanguage)
  }, [languageOptions, onLanguageChange])

  const renderLanguageOption = useCallback((language: VisibleLanguageOption) => {
    if (isSpoofed && isLoggedIn) {
      return <LanguageRow key={language.locale}>
        <SpoofLanguageLabel
          size="medium"
          title={language.nativeName}
          endIcon={currentLanguage?.locale === language.locale ? <LineCheckIcon /> : undefined}
          $selected={currentLanguage?.locale === language.locale}
        />
        <SpoofLanguageActions
          language={language}
          onChangeOnlyForSpoof={onLanguageChange}
        />
      </LanguageRow>
    }

    return (
      <LanguageRow key={language.locale}>
        <ClickableListItem
          size="medium"
          endIcon={
            currentLanguage?.locale === language.locale ? (
              <LineCheckIcon />
            ) : undefined
          }
          title={language.nativeName}
          data-locale={language.locale}
          onClick={onLanguageClick}
          selected={currentLanguage?.locale === language.locale}
        />
      </LanguageRow>
    )
  }, [currentLanguage?.locale, onLanguageClick, onLanguageChange, isSpoofed, isLoggedIn])

  const rows = [
    ...releasedLanguages.map(renderLanguageOption),
    showPreReleaseSeparator ? (
      <PreReleaseLanguageSeparator key="pre-release-language-separator" />
    ) : null,
    ...preReleaseLanguages.map(renderLanguageOption),
  ]

  return isSpoofed && isLoggedIn ? <ModalProvider>{rows}</ModalProvider> : rows
}

export default React.memo(NavBarLanguageItems)
